import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { BadgeTone, PdfCardsSection, PdfRegistryCard, PdfSection, PdfTableSection } from '../types/registryPdf';
import {
    CONTENT_BOTTOM,
    CONTENT_WIDTH,
    PAGE_PADDING_TOP,
    PAGE_PADDING_X,
    PDF_PAGE_HEIGHT,
    PDF_PAGE_WIDTH,
    REPORT_FONT_BOLD,
    REPORT_FONT_FAMILY,
    REPORT_FONT_REGULAR,
} from './registryPdfUtils';


// Размеры задаются в CSS-пикселях, как в вёрстке сводки, а в PDF переводятся в пункты
const PT_PER_PX = 72 / 96;
const PDF_FONT = 'PTSans';
// У PT Sans восходящие 1.018 em, нисходящие 0.276 em
const BASELINE_SHIFT_EM = (1.018 - 0.276) / 2;

const PROGRESS_SUMMARY_PERCENT = 10;
const PROGRESS_STEP = 10;
const ITEMS_PER_PROGRESS_UPDATE = 50;

const CARD_PADDING_Y = 16;
const CONTINUATION_GAP = 12;
const MIN_SPACE_FOR_SPLIT_CARD = 200;
const ASIDE_WIDTH = 236;
const COLUMNS_GAP = 12;
const MAIN_WIDTH = CONTENT_WIDTH - ASIDE_WIDTH - COLUMNS_GAP;
const ASIDE_PADDING = { top: 12, bottom: 14, x: 14 };
const MAIN_PADDING = { top: 12, bottom: 16, x: 18 };
const SECTION_HEADER_HEIGHT = 42;
const SECTION_TOP = PAGE_PADDING_TOP + SECTION_HEADER_HEIGHT;
const TABLE_TOP_GAP = 14;
const TABLE_CELL_PADDING_Y = 5;
const TABLE_MIN_FONT_SIZE = 6.5;

const COLORS = {
    text: '#1f2630',
    label: '#1d4f8f',
    hint: '#7a8492',
    sectionTitle: '#374151',
    brand: '#1d5ba8',
    divider: '#dfe3e8',
    footer: '#8a94a3',
    asideBackground: '#eaf1f9',
    mainBackground: '#f4f5f7',
    tableStripe: '#f4f6f9',
    white: '#ffffff',
};

const BADGE_COLORS: { [tone in BadgeTone]: { border: string; background: string; text: string } } = {
    neutral: { border: '#9aa3ae', background: '#e6e8eb', text: '#374151' },
    positive: { border: '#7eb246', background: '#e8f3dc', text: '#3d6b16' },
    negative: { border: '#ed5d62', background: '#fde7e8', text: '#a52a2f' },
    warning: { border: '#ffa600', background: '#fff2d6', text: '#8a5a00' },
};


let reportFonts: { regular: string; bold: string } | null = null;

const getFontUrl = (fileName: string) => `${process.env.PUBLIC_URL}/fonts/${fileName}`;

const readFontAsBase64 = async (fileName: string): Promise<string> => {
    const response = await fetch(getFontUrl(fileName));
    if (!response.ok) throw new Error(`Не удалось загрузить шрифт ${fileName}: ${response.status}`);
    const fontBlob = await response.blob();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(fontBlob);
    });
};

// Именно @font-face: html2canvas копирует стили в свой клон документа, а шрифт из FontFace API туда не попадает
const addFontFaceStyle = () => {
    if (document.getElementById('registry-pdf-fonts')) return;
    const style = document.createElement('style');
    style.id = 'registry-pdf-fonts';
    style.textContent = `
        @font-face { font-family: '${REPORT_FONT_FAMILY}'; src: url('${getFontUrl(REPORT_FONT_REGULAR)}') format('truetype'); font-weight: 400; }
        @font-face { font-family: '${REPORT_FONT_FAMILY}'; src: url('${getFontUrl(REPORT_FONT_BOLD)}') format('truetype'); font-weight: 700; }
    `;
    document.head.appendChild(style);
};

export const loadReportFonts = async () => {
    if (reportFonts) return reportFonts;
    addFontFaceStyle();
    const regular = await readFontAsBase64(REPORT_FONT_REGULAR);
    const bold = await readFontAsBase64(REPORT_FONT_BOLD);
    await document.fonts.load(`400 12px '${REPORT_FONT_FAMILY}'`);
    await document.fonts.load(`700 12px '${REPORT_FONT_FAMILY}'`);
    reportFonts = { regular, bold };
    return reportFonts;
};


const pt = (px: number) => px * PT_PER_PX;

const hexToRgb = (hex: string): [number, number, number] => [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
];

const setFont = (pdf: jsPDF, sizePx: number, bold: boolean, color: string) => {
    pdf.setFont(PDF_FONT, bold ? 'bold' : 'normal');
    pdf.setFontSize(pt(sizePx));
    pdf.setTextColor(...hexToRgb(color));
};

const getTextWidth = (pdf: jsPDF, text: string) => pdf.getTextWidth(text) / PT_PER_PX;

// Переносит текст по ширине с учётом переводов строк; неразрывные пробелы (разряды чисел) не трогаем
const wrapText = (pdf: jsPDF, text: string, widthPx: number): string[] => {
    const lines: string[] = [];
    text.split(/\r?\n/).forEach(paragraph => {
        const cleanParagraph = paragraph.replace(/[ \t]+/g, ' ').trim();
        if (!cleanParagraph) {
            lines.push('');
            return;
        }
        lines.push(...pdf.splitTextToSize(cleanParagraph, pt(widthPx)));
    });
    return lines;
};

const drawTextAtBaseline = (pdf: jsPDF, text: string, x: number, baseline: number, align: 'left' | 'right' | 'center' = 'left') => {
    pdf.text(text, pt(x), pt(baseline), { align });
};

// y — верх строки высотой lineHeight, как в CSS
const drawText = (pdf: jsPDF, text: string, x: number, y: number, sizePx: number, lineHeight: number, align: 'left' | 'right' | 'center' = 'left') => {
    drawTextAtBaseline(pdf, text, x, y + lineHeight / 2 + BASELINE_SHIFT_EM * sizePx, align);
};

const fillRect = (pdf: jsPDF, x: number, y: number, width: number, height: number, color: string, radius = 0) => {
    pdf.setFillColor(...hexToRgb(color));
    if (radius > 0) {
        pdf.roundedRect(pt(x), pt(y), pt(width), pt(height), pt(radius), pt(radius), 'F');
    } else {
        pdf.rect(pt(x), pt(y), pt(width), pt(height), 'F');
    }
};


// Карточка разбивается на строки, чтобы длинную карточку можно было продолжить на следующем листе
interface CardRow {
    height: number;
    isSpacer?: boolean;
    keepWithNext?: boolean;
    draw?: (x: number, y: number) => void;
}

const spacer = (height: number): CardRow => ({ height, isSpacer: true });

const textRows = (pdf: jsPDF, text: string, width: number, sizePx: number, lineHeight: number, bold: boolean, color: string, keepWithNext = false): CardRow[] => {
    setFont(pdf, sizePx, bold, color);
    return wrapText(pdf, text, width).map(line => ({
        height: lineHeight,
        keepWithNext,
        draw: (x: number, y: number) => {
            setFont(pdf, sizePx, bold, color);
            drawText(pdf, line, x, y, sizePx, lineHeight);
        },
    }));
};

const badgeRow = (pdf: jsPDF, label: string, tone: BadgeTone): CardRow => {
    const colors = BADGE_COLORS[tone];
    setFont(pdf, 10.5, true, colors.text);
    const badgeWidth = getTextWidth(pdf, label) + 22;
    return {
        height: 21,
        draw: (x: number, y: number) => {
            pdf.setFillColor(...hexToRgb(colors.background));
            pdf.setDrawColor(...hexToRgb(colors.border));
            pdf.setLineWidth(PT_PER_PX);
            pdf.roundedRect(pt(x), pt(y + 0.5), pt(badgeWidth), pt(20), pt(10), pt(10), 'FD');
            setFont(pdf, 10.5, true, colors.text);
            drawText(pdf, label, x + badgeWidth / 2, y + 0.5, 10.5, 20, 'center');
        },
    };
};

const getAsideRows = (pdf: jsPDF, card: PdfRegistryCard): CardRow[] => {
    const width = ASIDE_WIDTH - ASIDE_PADDING.x * 2;
    const rows = textRows(pdf, card.number || '—', width, 13, 17, true, COLORS.text);
    if (card.status) rows.push(spacer(8), badgeRow(pdf, card.status.text, card.status.tone));

    card.fields.forEach(field => {
        rows.push(spacer(8), ...textRows(pdf, field.label, width, 11, 15, true, COLORS.label, true));
        if (field.badge) {
            rows.push(spacer(2), badgeRow(pdf, field.badge.text, field.badge.tone));
            return;
        }
        const entries = field.entries.filter(entry => entry.value || entry.hint);
        if (entries.length === 0) {
            rows.push(...textRows(pdf, '—', width, 10.5, 14, false, COLORS.text));
            return;
        }
        entries.forEach((entry, entryIndex) => {
            if (entryIndex > 0) rows.push(spacer(4));
            rows.push(...textRows(pdf, entry.value || '—', width, 10.5, 14, false, COLORS.text));
            if (entry.hint) rows.push(spacer(1), ...textRows(pdf, entry.hint, width, 10, 13, false, COLORS.hint));
        });
    });
    return rows;
};

const getMainRows = (pdf: jsPDF, card: PdfRegistryCard): CardRow[] => {
    const width = MAIN_WIDTH - MAIN_PADDING.x * 2;
    const rows: CardRow[] = [];
    card.sections.forEach((section, sectionIndex) => {
        if (sectionIndex > 0) rows.push(spacer(14));
        rows.push(...textRows(pdf, section.title, width, 12.5, 19, true, COLORS.sectionTitle, true));
        rows.push(...textRows(pdf, section.text || '—', width, 10.5, 15, false, COLORS.text));
    });
    return rows;
};

const getRowsHeight = (rows: CardRow[]) => rows.reduce((sum, row) => sum + row.height, 0);

// Сколько строк колонки помещается на остаток листа; заголовок не оставляем последней строкой
const takeRows = (rows: CardRow[], startIndex: number, availableHeight: number) => {
    let index = startIndex;
    while (index < rows.length && rows[index].isSpacer) index++;
    const firstIndex = index;
    let height = 0;
    while (index < rows.length && height + rows[index].height <= availableHeight) {
        height += rows[index].height;
        index++;
    }
    while (index < rows.length && index > firstIndex + 1 && rows[index - 1].keepWithNext) {
        index--;
        height -= rows[index].height;
    }
    return { firstIndex, endIndex: index, height };
};


const drawSectionHeader = (pdf: jsPDF, title: string) => {
    const baseline = PAGE_PADDING_TOP + 15 + BASELINE_SHIFT_EM * 30;
    setFont(pdf, 30, true, COLORS.brand);
    drawTextAtBaseline(pdf, 'АПТС', PAGE_PADDING_X, baseline);
    const brandWidth = getTextWidth(pdf, 'АПТС');
    setFont(pdf, 16, true, COLORS.text);
    drawTextAtBaseline(pdf, title, PAGE_PADDING_X + brandWidth + 24, baseline);
    fillRect(pdf, PAGE_PADDING_X, SECTION_TOP - 2, CONTENT_WIDTH, 2, COLORS.brand);
};

const drawFooter = (pdf: jsPDF, pageNumber: number, pagesCount: number) => {
    fillRect(pdf, PAGE_PADDING_X, CONTENT_BOTTOM, CONTENT_WIDTH, 1, COLORS.divider);
    setFont(pdf, 10, false, COLORS.footer);
    const lineHeight = PDF_PAGE_HEIGHT - CONTENT_BOTTOM - 1;
    drawText(pdf, 'Аналитический центр АПТС', PAGE_PADDING_X, CONTENT_BOTTOM + 1, 10, lineHeight);
    drawText(pdf, `Страница ${pageNumber} из ${pagesCount}`, PDF_PAGE_WIDTH - PAGE_PADDING_X, CONTENT_BOTTOM + 1, 10, lineHeight, 'right');
};

// Клонируем только отчёт: копия всей страницы с таблицей и графиками очень медленная
const captureSummaryPage = (page: HTMLElement) => html2canvas(page, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: COLORS.white,
    ignoreElements: element => !(element.contains(page) || page.contains(element) || element.closest('head') !== null),
});

// В данных встречается «156 000-157 500»: без пробелов вокруг дефиса и неразрывных пробелов в числах такой диапазон рвётся посреди числа
const prepareTableCell = (text: string) => {
    return (text || '')
        .replace(/(\d)\s*[-–]\s*(?=\d)/g, '$1 - ')
        .replace(/(\d) (?=\d{3}(\D|$))/g, '$1 ');
};

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);


interface BuildRegistryPdfParams {
    summaryPages: HTMLElement[];
    sections: PdfSection[];
    onProgress?: (percent: number) => void;
}

export const buildRegistryPdf = async ({ summaryPages, sections, onProgress }: BuildRegistryPdfParams) => {
    const fonts = await loadReportFonts();
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
    pdf.addFileToVFS(REPORT_FONT_REGULAR, fonts.regular);
    pdf.addFont(REPORT_FONT_REGULAR, PDF_FONT, 'normal');
    pdf.addFileToVFS(REPORT_FONT_BOLD, fonts.bold);
    pdf.addFont(REPORT_FONT_BOLD, PDF_FONT, 'bold');

    let pagesCount = 0;
    const addPage = () => {
        if (pagesCount > 0) pdf.addPage();
        pagesCount++;
    };

    // Каждое обновление прогресса перерисовывает страницу, поэтому шлём его не чаще чем раз в 10%
    let lastPercent = 0;
    const setProgress = (percent: number) => {
        if (percent < 100 && percent - lastPercent < PROGRESS_STEP) return;
        lastPercent = percent;
        if (onProgress) onProgress(percent);
    };

    for (let pageIndex = 0; pageIndex < summaryPages.length; pageIndex++) {
        const canvas = await captureSummaryPage(summaryPages[pageIndex]);
        addPage();
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight(), undefined, 'FAST');
        setProgress(Math.round(((pageIndex + 1) / summaryPages.length) * PROGRESS_SUMMARY_PERCENT));
    }

    const itemsCount = sum(sections.map(section => (section.kind === 'cards' ? section.cards.length : section.rows.length))) || 1;
    let drawnCount = 0;
    const markDrawn = async () => {
        drawnCount++;
        if (drawnCount % ITEMS_PER_PROGRESS_UPDATE !== 0) return;
        setProgress(PROGRESS_SUMMARY_PERCENT + Math.round((drawnCount / itemsCount) * (100 - PROGRESS_SUMMARY_PERCENT)));
        // Отдаём управление браузеру, чтобы кнопка успела показать процент
        await new Promise(resolve => setTimeout(resolve, 0));
    };

    const drawCardsSection = async (section: PdfCardsSection) => {
        if (section.cards.length === 0) return;
        let y = SECTION_TOP;
        let cardsOnPage = 0;
        const startPage = () => {
            addPage();
            drawSectionHeader(pdf, section.title);
            y = SECTION_TOP;
            cardsOnPage = 0;
        };
        startPage();

        for (const card of section.cards) {
            const asideRows = getAsideRows(pdf, card);
            const mainRows = getMainRows(pdf, card);
            const cardHeight = CARD_PADDING_Y * 2 + Math.max(
                ASIDE_PADDING.top + getRowsHeight(asideRows) + ASIDE_PADDING.bottom,
                MAIN_PADDING.top + getRowsHeight(mainRows) + MAIN_PADDING.bottom,
            );

            if (cardsOnPage > 0) {
                const fitsOnPage = y + cardHeight <= CONTENT_BOTTOM;
                const fitsOnEmptyPage = cardHeight <= CONTENT_BOTTOM - SECTION_TOP;
                // Карточку длиннее листа начинаем на текущем, если на нём есть место
                if (!fitsOnPage && (fitsOnEmptyPage || CONTENT_BOTTOM - y < MIN_SPACE_FOR_SPLIT_CARD)) {
                    startPage();
                } else {
                    fillRect(pdf, PAGE_PADDING_X, y, CONTENT_WIDTH, 1, COLORS.divider);
                }
            }

            const asideX = PAGE_PADDING_X;
            const mainX = PAGE_PADDING_X + ASIDE_WIDTH + COLUMNS_GAP;
            let asideIndex = 0;
            let mainIndex = 0;
            let partTop = y + CARD_PADDING_Y;
            let isCardDrawn = false;
            while (!isCardDrawn) {
                const availableHeight = CONTENT_BOTTOM - partTop;
                const asidePart = takeRows(asideRows, asideIndex, availableHeight - ASIDE_PADDING.top - ASIDE_PADDING.bottom);
                const mainPart = takeRows(mainRows, mainIndex, availableHeight - MAIN_PADDING.top - MAIN_PADDING.bottom);
                if (asidePart.endIndex === asidePart.firstIndex && mainPart.endIndex === mainPart.firstIndex && partTop <= SECTION_TOP + CONTINUATION_GAP) {
                    throw new Error('Карточка реестра не помещается на лист');
                }
                const partHeight = Math.max(
                    ASIDE_PADDING.top + asidePart.height + ASIDE_PADDING.bottom,
                    MAIN_PADDING.top + mainPart.height + MAIN_PADDING.bottom,
                );
                fillRect(pdf, asideX, partTop, ASIDE_WIDTH, partHeight, COLORS.asideBackground, 6);
                fillRect(pdf, mainX, partTop, MAIN_WIDTH, partHeight, COLORS.mainBackground, 6);

                let rowTop = partTop + ASIDE_PADDING.top;
                asideRows.slice(asidePart.firstIndex, asidePart.endIndex).forEach(row => {
                    if (row.draw) row.draw(asideX + ASIDE_PADDING.x, rowTop);
                    rowTop += row.height;
                });
                rowTop = partTop + MAIN_PADDING.top;
                mainRows.slice(mainPart.firstIndex, mainPart.endIndex).forEach(row => {
                    if (row.draw) row.draw(mainX + MAIN_PADDING.x, rowTop);
                    rowTop += row.height;
                });

                asideIndex = asidePart.endIndex;
                mainIndex = mainPart.endIndex;
                y = partTop + partHeight + CARD_PADDING_Y;
                isCardDrawn = asideIndex >= asideRows.length && mainIndex >= mainRows.length;
                if (!isCardDrawn) {
                    startPage();
                    partTop = y + CONTINUATION_GAP;
                }
            }
            cardsOnPage++;
            await markDrawn();
        }
    };

    const drawTableSection = async (section: PdfTableSection) => {
        const isWide = section.columns.length > 8;
        const cellPaddingX = isWide ? 4 : 6;
        const tableRows = section.rows.map(row => row.map(prepareTableCell));

        // Ширины колонок как у table-layout: auto — колонка не уже самого длинного слова,
        // если и так не помещается — уменьшаем шрифт
        const measureColumns = (fontSize: number) => {
            return section.columns.map((column, columnIndex) => {
                let minWidth = 0;
                let fullWidth = 0;
                const measureText = (text: string) => {
                    (text || '').split(' ').forEach(word => {
                        minWidth = Math.max(minWidth, getTextWidth(pdf, word));
                    });
                    fullWidth = Math.max(fullWidth, getTextWidth(pdf, text || ''));
                };
                setFont(pdf, fontSize, true, COLORS.text);
                measureText(column.title);
                setFont(pdf, fontSize, false, COLORS.text);
                tableRows.forEach(row => measureText(row[columnIndex]));
                // jsPDF при переносе меряет чуть иначе, без запаса слово «впритык» рвётся
                const padding = cellPaddingX * 2 + 4;
                return { minWidth: minWidth + padding, fullWidth: fullWidth + padding };
            });
        };

        let fontSize = isWide ? 8.5 : 10;
        let columnSizes = measureColumns(fontSize);
        while (sum(columnSizes.map(size => size.minWidth)) > CONTENT_WIDTH && fontSize > TABLE_MIN_FONT_SIZE) {
            fontSize -= 0.5;
            columnSizes = measureColumns(fontSize);
        }

        const minTableWidth = sum(columnSizes.map(size => size.minWidth));
        const fullTableWidth = sum(columnSizes.map(size => size.fullWidth));
        const totalWeight = sum(section.columns.map(column => column.weight || 1));
        let columnWidths: number[];
        if (fullTableWidth <= CONTENT_WIDTH) {
            columnWidths = columnSizes.map((size, columnIndex) => (
                size.fullWidth + (CONTENT_WIDTH - fullTableWidth) * (section.columns[columnIndex].weight || 1) / totalWeight
            ));
        } else if (minTableWidth <= CONTENT_WIDTH) {
            columnWidths = columnSizes.map(size => (
                size.minWidth + (CONTENT_WIDTH - minTableWidth) * (size.fullWidth - size.minWidth) / (fullTableWidth - minTableWidth)
            ));
        } else {
            columnWidths = columnSizes.map(size => size.minWidth * CONTENT_WIDTH / minTableWidth);
        }

        const columnLefts: number[] = [];
        let left = PAGE_PADDING_X;
        columnWidths.forEach(width => {
            columnLefts.push(left);
            left += width;
        });

        const lineHeight = fontSize * 1.3;
        const wrapRow = (cells: string[], bold: boolean) => {
            setFont(pdf, fontSize, bold, COLORS.text);
            const cellLines = columnWidths.map((width, columnIndex) => wrapText(pdf, cells[columnIndex] || '—', width - cellPaddingX * 2));
            const linesCount = Math.max(...cellLines.map(lines => lines.length));
            return { cellLines, height: linesCount * lineHeight + TABLE_CELL_PADDING_Y * 2 };
        };
        const drawRow = (cellLines: string[][], top: number) => {
            cellLines.forEach((lines, columnIndex) => {
                lines.forEach((line, lineIndex) => {
                    drawText(pdf, line, columnLefts[columnIndex] + cellPaddingX, top + TABLE_CELL_PADDING_Y + lineIndex * lineHeight, fontSize, lineHeight);
                });
            });
        };

        const header = wrapRow(section.columns.map(column => column.title), true);
        let y = 0;
        const startPage = () => {
            addPage();
            drawSectionHeader(pdf, section.title);
            y = SECTION_TOP + TABLE_TOP_GAP;
            fillRect(pdf, PAGE_PADDING_X, y, CONTENT_WIDTH, header.height, COLORS.brand);
            setFont(pdf, fontSize, true, COLORS.white);
            drawRow(header.cellLines, y);
            y += header.height;
        };
        startPage();

        const rows = tableRows.length > 0 ? tableRows : [['Нет данных']];
        for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
            const row = wrapRow(rows[rowIndex], false);
            if (y + row.height > CONTENT_BOTTOM) startPage();
            if (rowIndex % 2 === 1) fillRect(pdf, PAGE_PADDING_X, y, CONTENT_WIDTH, row.height, COLORS.tableStripe);
            setFont(pdf, fontSize, false, COLORS.text);
            drawRow(row.cellLines, y);
            y += row.height;
            fillRect(pdf, PAGE_PADDING_X, y - 0.5, CONTENT_WIDTH, 0.5, COLORS.divider);
            await markDrawn();
        }
    };

    for (const section of sections) {
        if (section.kind === 'cards') {
            await drawCardsSection(section);
        } else {
            await drawTableSection(section);
        }
    }

    // Футер рисуем в конце, когда известно число листов
    for (let pageNumber = 1; pageNumber <= pagesCount; pageNumber++) {
        pdf.setPage(pageNumber);
        drawFooter(pdf, pageNumber, pagesCount);
    }
    setProgress(100);
    return pdf;
};
